import React, { useEffect, useRef, useState } from 'react';

/* TABLE À SAISIE — STANDARD (2026-09-04). Cellules à saisir dans une ligne de
   table : bleu de la charte, sans cadre. Un clic ouvre la saisie ; Entrée ou
   la sortie du champ enregistre, Échap annule ; au clavier, taper un caractère
   ouvre et remplace, comme dans un tableur. La table qui les porte prend la
   classe `lk-entry-table` (largeur fixe, colonnes déclarées par <colgroup>) :
   sans quoi un champ ouvert élargit sa colonne. Costume dans designSystem.css. */
/* COMME DANS EXCEL (demande d'Olivier, 2026-09-29) : les flèches, Tab et
   Entrée passent d'une cellule à la voisine ; Entrée enregistre et descend,
   Tab enregistre et passe à droite. Un COLLAGE venu d'Excel (plusieurs
   valeurs, tabulations et retours ligne) remplit les cellules à droite et
   dessous, une à une, chacune par son propre enregistrement. La voisine se
   cherche dans la table : même colonne, ligne suivante qui porte une cellule
   à saisir ; même ligne, colonne suivante. */
const SEL = '.ic-cell[role="button"]';
function neighbor(el, dRow, dCol) {
  const td = el && el.closest('td, th');
  if (!td) return null;
  const tr = td.parentElement;
  if (dCol) {
    for (let i = td.cellIndex + dCol; i >= 0 && i < tr.cells.length; i += dCol) {
      const b = tr.cells[i].querySelector(SEL);
      if (b) return b;
    }
    return null;
  }
  const rows = Array.from(tr.closest('table').rows);
  for (let r = rows.indexOf(tr) + dRow; r >= 0 && r < rows.length; r += dRow) {
    const c = rows[r].cells[td.cellIndex];
    const b = c && c.querySelector(SEL);
    if (b) return b;
  }
  return null;
}
/* UN CALCUL À LA SAISIE (demande d'Olivier, 2026-10-10) : une cellule qui
   l'accepte (`calc`) lit « 220*10 » ou « =220*10 » et enregistre le résultat,
   2'200 ; la formule n'est pas gardée. Les quatre opérations et les
   parenthèses, rien d'autre : aucun `eval`, une grammaire à la main. Une
   formule illisible renvoie `undefined` et rien ne s'enregistre. */
export function evalFormule(raw) {
  let s = String(raw ?? '').trim();
  if (s.startsWith('=')) s = s.slice(1);
  s = s.replace(/[\s'’]/g, '').replace(/,/g, '.').replace(/[×xX]/g, '*').replace(/[÷:]/g, '/');
  if (s === '') return null;
  if (!/^[0-9.+\-*/()]+$/.test(s)) return undefined;
  let i = 0;
  const lire = () => s[i];
  const facteur = () => {
    if (lire() === '-') { i += 1; return -facteur(); }
    if (lire() === '+') { i += 1; return facteur(); }
    if (lire() === '(') {
      i += 1;
      const v = somme(); // eslint-disable-line no-use-before-define
      if (s[i] !== ')') throw new Error('parenthèse');
      i += 1;
      return v;
    }
    const m = /^(\d+\.?\d*|\.\d+)/.exec(s.slice(i));
    if (!m) throw new Error('nombre');
    i += m[0].length;
    return parseFloat(m[0]);
  };
  const produit = () => {
    let v = facteur();
    while (lire() === '*' || lire() === '/') {
      const op = s[i]; i += 1;
      const w = facteur();
      v = op === '*' ? v * w : v / w;
    }
    return v;
  };
  const somme = () => {
    let v = produit();
    while (lire() === '+' || lire() === '-') {
      const op = s[i]; i += 1;
      const w = produit();
      v = op === '+' ? v + w : v - w;
    }
    return v;
  };
  try {
    const v = somme();
    if (i !== s.length || !Number.isFinite(v)) return undefined;
    return Math.round(v * 1e6) / 1e6;
  } catch (e) {
    return undefined;
  }
}
const parseValue = (raw, type, calc = false) => {
  const s = String(raw ?? '').trim();
  if (type !== 'number') return s;
  if (calc) return evalFormule(s);
  return s === '' ? null : (parseFloat(s.replace(/[\s'’]/g, '').replace(',', '.')) || 0);
};
// Une grille collée depuis Excel ; null pour une seule valeur (collage normal).
const pastedGrid = (e) => {
  const txt = (e.clipboardData && e.clipboardData.getData('text')) || '';
  if (!/[\t\n]/.test(txt.trim())) return null;
  return txt.replace(/\r/g, '').replace(/\n+$/, '').split('\n').map((l) => l.split('\t'));
};
const tick = () => new Promise((r) => setTimeout(r, 30));
/* UN COLLAGE NE SORT PAS DE SA ZONE (2026-10-22) : une table peut marquer ses cellules `data-zone` (le réalisé, le
   planifié…). Un collage qui part d'une cellule d'une zone s'arrête au bord de cette zone, au lieu d'enjamber une cellule
   en lecture seule pour écrire dans la zone voisine (du réalisé collé dans les mois à venir). */
const memeZone = (origin, cell) => {
  const z = origin && origin.dataset && origin.dataset.zone;
  if (!z) return true;
  const td = cell && cell.closest('td, th');
  return Boolean(td && td.dataset.zone === z);
};
async function spread(origin, grid, first) {
  // La cellule d'origine prend la première valeur ; les autres la suite, une à
  // une, pour qu'un enregistrement ne recouvre pas le précédent.
  await first(grid[0][0]);
  let rowStart = origin;
  for (let r = 0; r < grid.length; r += 1) {
    if (r > 0) { rowStart = rowStart && neighbor(rowStart, 1, 0); if (!rowStart || !memeZone(origin, rowStart)) return; }
    let cell = rowStart;
    for (let c = 0; c < grid[r].length; c += 1) {
      if (c > 0) { cell = cell && neighbor(cell, 0, 1); if (!cell || !memeZone(origin, cell)) break; }
      if (r === 0 && c === 0) continue;
      const detail = { raw: grid[r][c], done: null };
      cell.dispatchEvent(new CustomEvent('lk-cell-set', { detail }));
      await detail.done;
      await tick();
    }
  }
}

export function Cell({ value, type = 'text', format, toDraft, onSave, canWrite, placeholder = '…', calc = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const ref = useRef(null);
  const btn = useRef(null);
  const wasEditing = useRef(false);
  const refocus = useRef(false);     // vrai quand la saisie s'est fermée au clavier (Entrée, Échap)
  const move = useRef(null);         // [lignes, colonnes] où aller après l'enregistrement
  const skip = useRef(false);        // un collage ferme la saisie sans l'enregistrer
  useEffect(() => { if (editing && ref.current) { ref.current.focus(); if (!ref.current.dataset.typed) ref.current.select(); } }, [editing]);
  // Après Entrée ou Échap, la cellule garde le focus pour enchaîner, puis passe
  // à la voisine demandée ; après un clic ailleurs, le focus est déjà parti.
  useEffect(() => {
    if (!editing && wasEditing.current && refocus.current && btn.current) {
      const to = move.current && neighbor(btn.current, move.current[0], move.current[1]);
      (to || btn.current).focus();
    }
    wasEditing.current = editing;
    refocus.current = false;
    move.current = null;
  }, [editing]);
  // Une valeur collée plus haut ou à gauche arrive par cet événement.
  useEffect(() => {
    const el = btn.current;
    if (!el || editing || !canWrite) return undefined;
    const onSet = (e) => {
      const next = parseValue(e.detail.raw, type, calc);
      if (next !== undefined && String(next ?? '') !== String(value ?? '')) e.detail.done = Promise.resolve(onSave(next));
    };
    el.addEventListener('lk-cell-set', onSet);
    return () => el.removeEventListener('lk-cell-set', onSet);
  });
  const saveNow = (raw) => {
    const next = parseValue(raw, type, calc);
    return next !== undefined && String(next ?? '') !== String(value ?? '') ? Promise.resolve(onSave(next)) : Promise.resolve();
  };
  const onPaste = (e) => {
    const grid = pastedGrid(e);
    if (!grid) return;
    e.preventDefault();
    // La CELLULE de table sert de repère : le champ, lui, disparaît à la fermeture.
    const origin = (btn.current || ref.current).closest('td, th');
    if (editing) { skip.current = true; refocus.current = true; setEditing(false); }
    spread(origin, grid, saveNow);
  };
  const shown = format ? format(value) : (value ?? '');
  const ouvrir = (premier = null) => {
    skip.current = false;
    setDraft(premier != null ? premier : (toDraft ? toDraft(value) : String(value ?? '')));
    setEditing(true);
  };
  if (!canWrite) return <span className="ic-cell ic-cell--ro">{shown}</span>;
  if (!editing) {
    return (
      <span ref={btn} className="ic-cell" role="button" tabIndex={0}
            onClick={(e) => { e.stopPropagation(); ouvrir(); }}
            onPaste={onPaste}
            onKeyDown={(e) => {
              const arrows = { ArrowDown: [1, 0], ArrowUp: [-1, 0], ArrowRight: [0, 1], ArrowLeft: [0, -1] };
              const go = arrows[e.key] || (e.key === 'Tab' ? [0, e.shiftKey ? -1 : 1] : null);
              if (go) {
                const to = neighbor(e.currentTarget, go[0], go[1]);
                if (to) { e.preventDefault(); to.focus(); }
                return;
              }
              if (e.key === 'Enter' || e.key === 'F2' || e.key === ' ') { e.preventDefault(); ouvrir(); return; }
              // Comme dans un tableur : taper un caractère ouvre la saisie et remplace la valeur.
              if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); ouvrir(e.key); }
            }}>
        {shown === '' ? <span className="ic-cell--ph">{placeholder}</span> : shown}
      </span>
    );
  }
  const commit = () => {
    if (skip.current) { skip.current = false; return; }
    setEditing(false);
    const raw = String(draft).trim();
    const next = type === 'number'
      ? (raw === '' ? null : calc ? evalFormule(raw) : (parseFloat(raw.replace(/[\s'’]/g, '').replace(',', '.')) || 0))
      : raw;
    if (next === undefined) return;          // une formule illisible n'enregistre rien
    if (String(next ?? '') !== String(value ?? '')) onSave(next);
  };
  return (
    <input ref={ref} className="ic-cell ic-cell--edit" type="text" placeholder={placeholder === '…' ? '' : placeholder}
           data-typed={draft !== '' && draft !== String(value ?? '') && draft.length === 1 ? '1' : undefined}
           inputMode={type === 'number' && !calc ? 'decimal' : 'text'} value={draft}
           onClick={(e) => e.stopPropagation()}
           onChange={(e) => setDraft(e.target.value)} onBlur={commit}
           onPaste={onPaste}
           onKeyDown={(e) => {
             // Entrée descend, Tab passe à droite, les flèches verticales aussi.
             const after = e.key === 'Enter' ? [e.shiftKey ? -1 : 1, 0]
               : e.key === 'Tab' ? [0, e.shiftKey ? -1 : 1]
               : e.key === 'ArrowDown' ? [1, 0] : e.key === 'ArrowUp' ? [-1, 0] : null;
             if (after) { e.preventDefault(); refocus.current = true; move.current = after; commit(); }
             if (e.key === 'Escape') { refocus.current = true; setEditing(false); }
           }} />
  );
}

export function Choix({ value, options, onSave, canWrite, emptyLabel = '—', tone = '' }) {
  const found = options.find((o) => o.value === (value ?? ''));
  if (!canWrite) return <span className={`ic-cell ic-cell--ro ${tone}`}>{found ? found.label : emptyLabel}</span>;
  // Un <select> prend la largeur de sa plus longue option : on le ramène à celle de la valeur choisie.
  const label = found ? found.label : emptyLabel;
  return (
    <select className={`ic-cell ic-select ${tone}`} value={value ?? ''} onClick={(e) => e.stopPropagation()}
            style={{ width: `${Math.min(label.length + 1.5, 28)}ch` }}
            onChange={(e) => onSave(e.target.value || null)}>
      {!found && <option value="">{emptyLabel}</option>}
      {/* Une option indisponible se lit grisée, avec sa raison (2026-09-28). */}
      {options.map((o) => <option key={o.value} value={o.value} disabled={!!o.disabled} title={o.title}>{o.label}</option>)}
    </select>
  );
}
